import React, { useState, useEffect } from 'react';
import { Calendar, Users, CheckCircle2, TrendingUp, RefreshCw, UserCheck } from 'lucide-react';

const MonthlyInscriptionsTracker = ({ anneeScolaire = '2026/2027' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      const res = await fetch(`   https://vmi1977988.contaboserver.net/api2/etudiants/inscrits-par-mois?anneeScolaire=${encodeURIComponent(anneeScolaire)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) {
        throw new Error(`Erreur serveur (${res.status})`);
      }

      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error('Erreur fetch inscrits par mois:', err);
      setError(err.message || 'Erreur lors de la récupération des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [anneeScolaire]);

  const list = data?.statistiquesParMois || [];
  const totalInscrits = data?.totalInscrits || list.reduce((sum, item) => sum + item.count, 0);
  const totalPayes = list.reduce((sum, item) => sum + (item.payesCount || 0), 0);

  return (
    <div
      style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: '2rem',
        border: '1px solid #e5e7eb'
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ background: '#ecfdf5', padding: '0.6rem', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Calendar size={24} style={{ color: '#059669' }} />
          </div>
          <div>
            <h2
              style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: '#1f2937',
                margin: 0
              }}
            >
              Suivi Inscriptions par Mois {anneeScolaire}
            </h2>
            <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              Nouveaux étudiants (Sup2i Aïn Sebaâ)
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: '#f3f4f6', padding: '0.5rem 1rem', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <UserCheck size={18} style={{ color: '#2563eb' }} />
            <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#1f2937' }}>
              Total : <span style={{ color: '#2563eb' }}>{totalInscrits}</span>
            </span>
          </div>

          <button
            onClick={fetchStats}
            title="Rafraîchir"
            style={{
              background: '#f3f4f6',
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyInscrits: 'center',
              color: '#4b5563',
              transition: 'all 0.2s'
            }}
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Contenu */}
      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
          <RefreshCw size={24} style={{ margin: '0 auto 0.5rem', animation: 'spin 1s linear infinite' }} />
          <p style={{ margin: 0 }}>Chargement du suivi des inscriptions...</p>
        </div>
      ) : error ? (
        <div style={{ padding: '1.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b' }}>
          ❌ {error}
        </div>
      ) : list.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: '8px' }}>
          Aucune nouvelle inscription enregistrée pour l'année scolaire {anneeScolaire}.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f9fafb' }}>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'left', fontWeight: '700', color: '#374151', borderRight: '1px solid #e5e7eb' }}>
                  Mois
                </th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'center', fontWeight: '700', color: '#374151', borderRight: '1px solid #e5e7eb' }}>
                  Nbr. Nouveaux Inscrits
                </th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'center', fontWeight: '700', color: '#374151', borderRight: '1px solid #e5e7eb' }}>
                  Étudiants Payés
                </th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'center', fontWeight: '700', color: '#374151', borderRight: '1px solid #e5e7eb' }}>
                  Taux de Paiement
                </th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: '700', color: '#374151' }}>
                  Part du Total
                </th>
              </tr>
            </thead>
            <tbody>
              {list.map((item, idx) => {
                const pctPaiement = item.count > 0 ? Math.round((item.payesCount / item.count) * 100) : 0;
                const partTotal = totalInscrits > 0 ? Math.round((item.count / totalInscrits) * 100) : 0;

                return (
                  <tr
                    key={item.cle || idx}
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      background: idx % 2 === 0 ? '#ffffff' : '#fafafa',
                      transition: 'background 0.15s'
                    }}
                  >
                    {/* Nom du mois */}
                    <td style={{ padding: '0.85rem 1rem', fontWeight: '600', color: '#1f2937', borderRight: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#059669' }}></span>
                        {item.label}
                      </div>
                    </td>

                    {/* Nombre inscrits */}
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                      <span
                        style={{
                          background: '#dbeafe',
                          color: '#1e40af',
                          padding: '0.35rem 0.85rem',
                          borderRadius: '9999px',
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}
                      >
                        {item.count}
                      </span>
                    </td>

                    {/* Payés */}
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                      <span
                        style={{
                          background: item.payesCount > 0 ? '#dcfce7' : '#f3f4f6',
                          color: item.payesCount > 0 ? '#166534' : '#9ca3af',
                          padding: '0.35rem 0.85rem',
                          borderRadius: '9999px',
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}
                      >
                        {item.payesCount}
                      </span>
                    </td>

                    {/* Taux de paiement */}
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                        <div style={{ width: '60px', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${pctPaiement}%`, height: '100%', background: pctPaiement > 50 ? '#10b981' : '#f59e0b' }}></div>
                        </div>
                        <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#374151' }}>
                          {pctPaiement}%
                        </span>
                      </div>
                    </td>

                    {/* Part du total */}
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: '600', color: '#4b5563' }}>
                      {partTotal}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: '#f0fdf4', borderTop: '2px solid #22c55e', fontWeight: 'bold', color: '#15803d' }}>
                <td style={{ padding: '1rem', fontWeight: 'bold', borderRight: '1px solid #e5e7eb' }}>
                  Total ({list.length} mois)
                </td>
                <td style={{ padding: '1rem', textAlign: 'center', borderRight: '1px solid #e5e7eb', fontSize: '1.05rem' }}>
                  {totalInscrits}
                </td>
                <td style={{ padding: '1rem', textAlign: 'center', borderRight: '1px solid #e5e7eb', fontSize: '1.05rem' }}>
                  {totalPayes}
                </td>
                <td style={{ padding: '1rem', textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
                  {totalInscrits > 0 ? Math.round((totalPayes / totalInscrits) * 100) : 0}%
                </td>
                <td style={{ padding: '1rem', textAlign: 'right' }}>
                  100%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default MonthlyInscriptionsTracker;
